import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import PasswordField from "@/components/PasswordField";
import { loginCustomer } from "@/lib/customerAuth";

/**
 * /account/login (Design Specification Section 12.2). One generic error for unknown email, wrong password,
 * and a locked account (AC-ACCT-1) — the server already answers with one message; the page never adds its
 * own guess about which case it was.
 */
export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await loginCustomer(email.trim(), password);
      const to = (location.state && location.state.from) || "/account";
      navigate(to, { replace: true });
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 429) setError("Too many attempts. Please try again later.");
      else if (status === 503) setError("Accounts are not available right now.");
      else setError("Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[480px] mx-auto px-6 py-16" data-testid="account-signin-page">
      <Seo title="Sign in" noindex path="/account/login" />
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-6">Sign in</h1>

      {error && (
        <p role="alert" className="border-2 border-[#B91C1C] bg-[#FEF2F2] text-[#B91C1C] rounded-xl p-3 mb-5" data-testid="signin-error">
          {error}
        </p>
      )}

      <form onSubmit={submit} noValidate className="space-y-5" data-testid="signin-form">
        <div>
          <label htmlFor="signin-email" className="text-overline text-zinc-500 mb-1 block">Email</label>
          <input
            id="signin-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-brutal"
            data-testid="field-email"
          />
        </div>
        <PasswordField
          id="signin-password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          maxLength={256}
        />
        <button type="submit" disabled={submitting} aria-busy={submitting} className="btn-green w-full" data-testid="signin-submit">
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="mt-6 text-sm text-slate-600">
        <Link to="/account/register" className="underline">Create an account</Link>
      </p>
      {/* "Forgot your password?" is held: no password-reset endpoint exists yet (Design Specification Section
          12.2, open item O13-adjacent). Do not link to a page that cannot do anything. */}
    </div>
  );
}
